import { useState, useCallback, useEffect, useRef } from 'react';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import { useAuth } from '../contexts/AuthContext';
import { useLocalStorage } from './use-local-storage';
import { debounce } from '../utils/debounce';

export type ContentItem = {
  id: string;
  type: 'post' | 'profile';
  content: any;
  author: any;
  emotionalMapping: {
    primaryEmotion: string;
    secondaryEmotions: string[];
    useContext: string[];
  };
  relevanceScore: number;
};

export type ContextStrategy = {
  contentTypes: string[];
  emotionalTones: string[];
  freshness: number;
  relevance: number;
  creatorTypes: string[];
};

export type ContextAnalysis = {
  emotionalState: string;
  needs: string[];
  interests: string[];
  timeContext: string;
};

export interface ContextEngineResult {
  results: ContentItem[];
  context: ContextAnalysis;
  strategy: ContextStrategy;
  sessionId: string;
}

export function useContextEngine() {
  const supabase = useSupabaseClient();
  const { user } = useAuth();
  
  // Check if Supabase functions API is initialized
  const [isFunctionsAvailable, setIsFunctionsAvailable] = useState<boolean>(false);
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState<ContentItem[]>([]);
  const [contextAnalysis, setContextAnalysis] = useState<ContextAnalysis | null>(null);
  const [contentStrategy, setContentStrategy] = useState<ContextStrategy | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Session management for conversation continuity
  const [sessionId, setSessionId] = useLocalStorage<string>('fomo_context_session_id', '');
  const [previousExpressions, setPreviousExpressions] = useLocalStorage<string[]>('fomo_previous_expressions', []);
  
  // Cache results for better UX
  const resultsCache = useRef<Map<string, { data: ContextEngineResult, timestamp: number }>>(new Map());
  const CACHE_TTL = 1000 * 60 * 15; // 15 minutes
  
  // Function to process user expression through context engine
  const processExpression = useCallback(async (expression: string) => {
    if (!expression.trim()) {
      setError('Please enter your state or what you\'re looking for');
      return;
    }

    setIsSearching(true);
    setError(null);
    
    // Check cache first
    const cacheKey = expression.toLowerCase().trim();
    const cachedResult = resultsCache.current.get(cacheKey);
    
    if (cachedResult && (Date.now() - cachedResult.timestamp < CACHE_TTL)) {
      console.log('Using cached context engine result for:', expression);
      setResults(cachedResult.data.results);
      setContextAnalysis(cachedResult.data.context);
      setContentStrategy(cachedResult.data.strategy);
      setIsSearching(false);
      return;
    }
    
    try {
      // Prepare conversation context
      const previousContext = previousExpressions.length > 0 ? {
        expressions: previousExpressions.slice(-3), // Last 3 expressions
        responses: [], // We're not storing full responses yet
        timestamp: Date.now()
      } : undefined;
      
      // Call context engine
      console.log('Invoking context-engine with expression:', expression);
      
      // Check if functions API is available
      if (!isFunctionsAvailable) {
        console.warn('[Context Engine] Supabase functions API is not available - feature disabled');
        setError('Semantic search is not available. Using basic search instead.');
        setIsSearching(false);
        return;
      }

      const { data, error: searchError } = await supabase.functions.invoke('context-engine', {
        body: {
          expression,
          sessionId: sessionId || undefined,
          userId: user?.id,
          previousContext
        }
      });
      
      console.log('Context engine response:', { data, searchError });
      
      if (searchError) {
        console.error('Context engine error:', searchError);
        setError(searchError.message || 'Failed to process your expression');
        setIsSearching(false);
        return;
      }
      
      if (!data) {
        console.error('Context engine returned no data');
        setError('No results from context engine');
        setIsSearching(false);
        return;
      }
      
      // Update state with results
      setResults(data.results);
      setContextAnalysis(data.context);
      setContentStrategy(data.strategy);
      
      // Update session management
      if (data.sessionId) {
        setSessionId(data.sessionId);
      }
      
      // Update expression history
      const newExpressions = [...previousExpressions, expression].slice(-10); // Keep last 10
      setPreviousExpressions(newExpressions);
      
      // Cache results
      resultsCache.current.set(cacheKey, {
        data,
        timestamp: Date.now()
      });
      
      // Track search analytics
      if (user?.id) {
        // Don't await this - non-blocking analytics
        try {
          trackExpressionAnalytics(expression, data.results.length, user.id)
            .catch(err => console.warn('Analytics tracking failed:', err));
        } catch (err) {
          console.warn('Failed to initiate analytics tracking:', err);
        }
      }
      
    } catch (err) {
      console.error('Error processing expression:', err);
      setError('Something went wrong processing your expression');
    } finally {
      setIsSearching(false);
    }
  }, [supabase, user, sessionId, previousExpressions, setSessionId, setPreviousExpressions]);
  
  // Debounced version for input fields
  const debouncedProcessExpression = useCallback(
    debounce((expression: string) => processExpression(expression), 500),
    [processExpression]
  );
  
  // Track expression analytics
  const trackExpressionAnalytics = useCallback(async (
    expression: string, 
    resultCount: number,
    userId: string
  ) => {
    try {
      await supabase.from('contextsearch_history').insert({
        user_id: userId,
        original_query: expression,
        results_returned: resultCount,
        time_of_day: new Date().getHours(),
        day_of_week: new Date().getDay()
      });
      console.log('Successfully tracked search analytics');
    } catch (err) {
      console.error('Failed to track expression:', err);
      // Log specific error details
      if (err instanceof Error) {
        console.error('Error message:', err.message);
        console.error('Error stack:', err.stack);
      }
    }
  }, [supabase]);
  
  // Check for Supabase Functions API availability
  useEffect(() => {
    // Check if functions API is available
    const checkFunctionsAvailability = () => {
      const isAvailable = supabase && supabase.functions && typeof supabase.functions.invoke === 'function';
      setIsFunctionsAvailable(isAvailable);
      return isAvailable;
    };
    
    // Initial check (only log once)
    const isAvailable = checkFunctionsAvailability();
    
    if (!isAvailable) {
      console.warn('[Context Engine] Supabase Functions API not available - semantic search disabled. To enable, start edge functions locally.');
      
      // Single retry after 3 seconds (in case functions are still loading)
      const retryTimeout = setTimeout(() => {
        const retryAvailable = checkFunctionsAvailability();
        if (retryAvailable) {
          console.log('[Context Engine] Functions API now available');
        }
      }, 3000);
      
      return () => clearTimeout(retryTimeout);
    } else {
      console.log('[Context Engine] Functions API is available');
    }
  }, [supabase]);
  
  // Clean up stale cache entries
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      const now = Date.now();
      resultsCache.current.forEach((value, key) => {
        if (now - value.timestamp > CACHE_TTL) {
          resultsCache.current.delete(key);
        }
      });
    }, 1000 * 60 * 5); // Every 5 minutes
    
    return () => clearInterval(cleanupInterval);
  }, []);
  
  // Return the hook interface
  return {
    processExpression,
    debouncedProcessExpression,
    isSearching,
    results,
    contextAnalysis,
    contentStrategy,
    error,
    sessionId,
    isFunctionsAvailable,
  };
}
